import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import InstallAppButton from "../components/InstallAppButton";

const NAV_LOGO_SRC = "/icons/pwp-192.png";

function AnimatedBackground() {
  return (
    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute inset-0 bg-white" />
      <div className="absolute top-[-10%] left-[-5%] h-[360px] w-[360px] rounded-full bg-yellow-200/40 blur-3xl" />
      <div className="absolute top-[8%] right-[-8%] h-[420px] w-[420px] rounded-full bg-purple-200/40 blur-3xl" />
      <div className="absolute bottom-[-12%] left-[18%] h-[380px] w-[380px] rounded-full bg-fuchsia-100/40 blur-3xl" />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(15,23,42,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.025)_1px,transparent_1px)] bg-[size:56px_56px]" />
    </div>
  );
}

function MenuBar({ nav, onHowItWorks }) {
  const { session, loading, signOut } = useAuth();
  const [busy, setBusy] = useState(false);
  const hasSession = Boolean(session);

  const onLogin = () => {
    const back = window.location.pathname + (window.location.search || "");
    nav(`/login?back=${encodeURIComponent(back)}`);
  };

  const onLogout = async () => {
    setBusy(true);
    try {
      await signOut();
    } catch (err) {
      console.warn("Logout error:", err);
    } finally {
      setBusy(false);
      nav("/home", { replace: true });
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 sm:px-5">
        <button
          type="button"
          onClick={() => nav("/home")}
          className="flex items-center gap-2 text-left"
          style={{ touchAction: "manipulation" }}
        >
          <img
            src={NAV_LOGO_SRC}
            alt="Piano With Pan"
            className="h-10 w-10 rounded-xl object-contain"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <div>
            <div
              className="text-base font-black text-slate-900 sm:text-2xl"
              style={{ fontFamily: "'Fredoka One', cursive" }}
            >
              Piano With Pan™
            </div>
            <div className="hidden text-[11px] font-bold text-slate-500 sm:block">
              Beginner piano made playful
            </div>
          </div>
        </button>

        <nav className="ml-auto hidden items-center gap-5 md:flex">
          <div style={{ touchAction: "manipulation" }}>
            <InstallAppButton />
          </div>

          <button
            type="button"
            onClick={onHowItWorks}
            className="text-sm font-bold text-slate-700 transition hover:text-slate-900"
          >
            How It Works
          </button>

          <button
            type="button"
            onClick={() => nav("/parents")}
            className="text-sm font-bold text-slate-700 transition hover:text-slate-900"
          >
            For Parents
          </button>

          {!loading && hasSession ? (
            <button
              type="button"
              onClick={onLogout}
              disabled={busy}
              className="rounded-full border-2 border-red-200 px-5 py-2 font-bold text-red-700 transition hover:bg-red-50 disabled:opacity-60"
            >
              {busy ? "..." : "Logout"}
            </button>
          ) : (
            <button
              type="button"
              onClick={onLogin}
              className="rounded-full bg-slate-900 px-5 py-2 font-bold text-white transition hover:bg-slate-800"
            >
              Log In
            </button>
          )}
        </nav>

        <div className="ml-auto flex items-center gap-2 md:hidden">
          <button
            type="button"
            onClick={onHowItWorks}
            className="rounded-full border border-purple-200 bg-purple-50 px-3 py-1.5 text-[11px] font-extrabold text-purple-900"
          >
            HOW IT WORKS
          </button>

          {!loading && hasSession ? (
            <button
              type="button"
              onClick={onLogout}
              disabled={busy}
              className="rounded-full border-2 border-red-300 px-3 py-1.5 text-xs font-bold text-red-700"
            >
              {busy ? "..." : "OUT"}
            </button>
          ) : (
            <button
              type="button"
              onClick={onLogin}
              className="rounded-full bg-slate-900 px-4 py-1.5 text-xs font-extrabold text-white"
            >
              LOG IN
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

function FloatingCTA({ show, onClick }) {
  if (!show) return null;

  return (
    <div className="fixed bottom-5 left-1/2 z-40 -translate-x-1/2 animate-[slideUp_0.25s_ease-out]">
      <button
        type="button"
        onClick={onClick}
        className="rounded-full border-2 border-yellow-500 bg-gradient-to-r from-yellow-400 to-amber-400 px-7 py-3 text-sm font-black text-slate-900 shadow-[0_12px_40px_rgba(251,191,36,0.35)] transition hover:scale-105 active:scale-95 sm:text-base"
        style={{ fontFamily: "'Fredoka One', cursive" }}
      >
        Get Started →
      </button>
    </div>
  );
}

function HeroVisual() {
  const cards = [
    {
      tag: "STEP 1",
      title: "Learn the white notes",
      body: "Simple note names. Easy first wins.",
      accent: "from-yellow-200 to-yellow-50 border-yellow-300",
      emoji: "🎹",
    },
    {
      tag: "STEP 2",
      title: "Make Doron sleep",
      body: "Play Twinkle Twinkle Little Star.",
      accent: "from-purple-200 to-purple-50 border-purple-300",
      emoji: "🐉",
    },
    {
      tag: "STEP 3",
      title: "Unlock more songs",
      body: "Black notes next, then Happy Birthday.",
      accent: "from-fuchsia-200 to-fuchsia-50 border-fuchsia-300",
      emoji: "🎵",
    },
  ];

  return (
    <div className="relative mx-auto grid max-w-5xl gap-4 lg:grid-cols-[1.15fr_0.95fr] lg:items-center">
      <div className="relative overflow-hidden rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)] sm:p-8">
        <div className="absolute right-5 top-5 rounded-full border border-yellow-300 bg-yellow-100 px-3 py-1 text-[11px] font-black text-yellow-900">
          AGES 5+
        </div>

        <div className="max-w-2xl">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-3 py-1.5 text-[11px] font-black text-purple-800">
            TURNING PRACTICE INTO PLAY
          </div>

          <h1
            className="text-4xl leading-tight text-slate-900 sm:text-5xl lg:text-6xl"
            style={{ fontFamily: "'Fredoka One', cursive" }}
          >
            Start your child’s musical journey today
          </h1>

          <p className="mt-4 max-w-xl text-sm font-bold leading-7 text-slate-600 sm:text-base">
            Turning practice into play with fun, beginner-friendly piano songs.
          </p>

          <p className="mt-3 max-w-2xl text-sm font-bold leading-7 text-slate-600 sm:text-base">
            Start with white notes, make the dragon sleep with <span className="text-slate-900">Twinkle Twinkle</span>,
            then unlock black notes and songs like <span className="text-slate-900">Happy Birthday</span>.
          </p>

          <div className="mt-5 flex flex-wrap gap-2">
            {[
              "No complex sheet music first",
              "Easy note names like A B C",
              "Real songs from the start",
              "Virtual piano included",
            ].map((item) => (
              <div
                key={item}
                className="rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-extrabold text-slate-700 sm:text-xs"
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4">
        {cards.map((card) => (
          <div
            key={card.title}
            className={`rounded-[28px] border bg-gradient-to-br ${card.accent} p-5 shadow-[0_14px_40px_rgba(15,23,42,0.06)]`}
          >
            <div className="mb-3 flex items-center justify-between">
              <span className="rounded-full bg-white/80 px-3 py-1 text-[10px] font-black tracking-wide text-slate-700">
                {card.tag}
              </span>
              <span className="text-3xl">{card.emoji}</span>
            </div>
            <h3
              className="text-xl text-slate-900"
              style={{ fontFamily: "'Fredoka One', cursive" }}
            >
              {card.title}
            </h3>
            <p className="mt-2 text-sm font-bold leading-6 text-slate-700">{card.body}</p>
            <div className="mt-4 rounded-2xl border border-white/70 bg-white/70 px-4 py-3 text-xs font-extrabold text-slate-600">
              Add your Treble Town characters / screenshot here
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuickActionBar({ onStart, onHowItWorks }) {
  return (
    <div className="mt-8 flex flex-col items-center gap-3">
      <div className="flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          onClick={onStart}
          className="rounded-full border-2 border-yellow-500 bg-gradient-to-r from-yellow-400 to-amber-400 px-8 py-4 text-base font-black text-slate-900 shadow-[0_12px_35px_rgba(251,191,36,0.35)] transition hover:scale-105 active:scale-95 sm:text-lg"
          style={{ fontFamily: "'Fredoka One', cursive" }}
        >
          Get Started →
        </button>

        <button
          type="button"
          onClick={onHowItWorks}
          className="rounded-full border-2 border-slate-200 bg-white px-8 py-4 text-base font-black text-slate-900 transition hover:border-slate-300 hover:bg-slate-50 sm:text-lg"
          style={{ fontFamily: "'Fredoka One', cursive" }}
        >
          See How It Works
        </button>
      </div>

      <p className="max-w-3xl text-center text-xs font-bold leading-6 text-slate-500 sm:text-sm">
        Best with a keyboard or piano. Beginner-friendly from day one. No instrument yet?
        Start right away with the virtual piano.
      </p>
    </div>
  );
}

function ProcessSection() {
  const items = [
    {
      title: "White notes first",
      body: "Children begin with easy, beginner-friendly notes instead of heavy theory.",
      icon: "🤍",
      tone: "border-yellow-200 bg-yellow-50",
    },
    {
      title: "Play Twinkle Twinkle early",
      body: "Kids quickly turn those notes into a real song and help Doron fall asleep.",
      icon: "⭐",
      tone: "border-purple-200 bg-purple-50",
    },
    {
      title: "Black notes next",
      body: "Once confidence grows, they unlock more notes without feeling overwhelmed.",
      icon: "🖤",
      tone: "border-fuchsia-200 bg-fuchsia-50",
    },
    {
      title: "More fun songs",
      body: "Then they move on to songs like Happy Birthday and keep building skill through play.",
      icon: "🎂",
      tone: "border-sky-200 bg-sky-50",
    },
  ];

  return (
    <section id="how-it-works" className="mt-16 sm:mt-20">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-black text-slate-700">
          HOW IT WORKS
        </div>
        <h2
          className="mt-4 text-3xl text-slate-900 sm:text-4xl"
          style={{ fontFamily: "'Fredoka One', cursive" }}
        >
          Learn simple notes. Play fun songs fast.
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-sm font-bold leading-7 text-slate-600 sm:text-base">
          No complex sheet music first. No overwhelming sight reading. Just clear, easy steps that
          help beginners feel successful early.
        </p>
      </div>

      <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {items.map((item) => (
          <div key={item.title} className={`rounded-[28px] border p-5 shadow-sm ${item.tone}`}>
            <div className="mb-3 text-3xl">{item.icon}</div>
            <h3
              className="text-xl text-slate-900"
              style={{ fontFamily: "'Fredoka One', cursive" }}
            >
              {item.title}
            </h3>
            <p className="mt-2 text-sm font-bold leading-6 text-slate-700">{item.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ParentValueSection() {
  const items = [
    {
      title: "Beginner-friendly",
      desc: "Built for children starting from zero.",
      icon: "🌱",
    },
    {
      title: "Real learning",
      desc: "Notes, rhythm, songs, and confidence grow together.",
      icon: "🎼",
    },
    {
      title: "Short sessions",
      desc: "Easy to fit into a real family routine.",
      icon: "⏱️",
    },
    {
      title: "Motivating experience",
      desc: "Stars, characters, and small wins keep kids engaged.",
      icon: "✨",
    },
  ];

  return (
    <section className="mt-16 sm:mt-20">
      <div className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.06)] sm:p-8">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-3 py-1.5 text-[11px] font-black text-purple-800">
            FOR PARENTS
          </div>
          <h2
            className="mt-4 text-3xl text-slate-900 sm:text-4xl"
            style={{ fontFamily: "'Fredoka One', cursive" }}
          >
            Real learning. Beginner-friendly from day one.
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm font-bold leading-7 text-slate-600 sm:text-base">
            The child experiences the magic. The parent sees the progress.
          </p>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {items.map((item) => (
            <div
              key={item.title}
              className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 transition hover:-translate-y-1 hover:bg-white hover:shadow-md"
            >
              <div className="mb-3 text-3xl">{item.icon}</div>
              <h3
                className="text-lg text-slate-900"
                style={{ fontFamily: "'Fredoka One', cursive" }}
              >
                {item.title}
              </h3>
              <p className="mt-2 text-sm font-bold leading-6 text-slate-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialSection() {
  const quotes = [
    {
      quote: "Now my son practices the piano on his own.",
      badge: "PARENT FEEDBACK",
    },
    {
      quote: "My child now reminds me about her piano practice.",
      badge: "PARENT FEEDBACK",
    },
  ];

  return (
    <section className="mt-16 sm:mt-20">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-yellow-300 bg-yellow-50 px-3 py-1.5 text-[11px] font-black text-yellow-900">
          SMALL WINS THAT MATTER
        </div>
        <h2
          className="mt-4 text-3xl text-slate-900 sm:text-4xl"
          style={{ fontFamily: "'Fredoka One', cursive" }}
        >
          When practice starts happening on its own
        </h2>
      </div>

      <div className="mt-8 grid gap-4 md:grid-cols-2">
        {quotes.map((item, idx) => (
          <div
            key={idx}
            className="rounded-[30px] border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-6 shadow-[0_16px_40px_rgba(15,23,42,0.05)]"
          >
            <div className="mb-4 inline-flex rounded-full border border-purple-200 bg-purple-50 px-3 py-1 text-[10px] font-black text-purple-800">
              {item.badge}
            </div>
            <p className="text-lg font-black leading-8 text-slate-900 sm:text-xl">
              “{item.quote}”
            </p>
            <div className="mt-4 text-xs font-extrabold uppercase tracking-wide text-slate-500">
              — Parent
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIdx, setOpenIdx] = useState(0);

  const faqs = [
    {
      q: "Do we need a keyboard or piano?",
      a: "Piano With Pan works best with a keyboard or piano, because the goal is real music learning. But families can still begin right away using the built-in virtual piano.",
    },
    {
      q: "What age is this for?",
      a: "It is designed for beginners, especially children ages 5 and above.",
    },
    {
      q: "Do kids need to read sheet music first?",
      a: "No. Children begin with simple note names and easy songs first, so the experience feels clear and welcoming.",
    },
    {
      q: "How long should practice be?",
      a: "About 10 to 15 minutes a day is enough to build confidence and consistency.",
    },
  ];

  return (
    <section className="mt-16 sm:mt-20">
      <div className="text-center">
        <h2
          className="text-3xl text-slate-900 sm:text-4xl"
          style={{ fontFamily: "'Fredoka One', cursive" }}
        >
          Questions parents ask
        </h2>
      </div>

      <div className="mx-auto mt-8 max-w-3xl space-y-3">
        {faqs.map((faq, idx) => {
          const open = openIdx === idx;
          return (
            <div key={faq.q} className="overflow-hidden rounded-[22px] border border-slate-200 bg-white shadow-sm">
              <button
                type="button"
                onClick={() => setOpenIdx(open ? -1 : idx)}
                className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
              >
                <span className="text-sm font-black text-slate-900 sm:text-base">{faq.q}</span>
                <span className="text-xl font-black text-slate-400">{open ? "−" : "+"}</span>
              </button>
              {open && (
                <div className="border-t border-slate-100 px-5 pb-5 pt-3 text-sm font-bold leading-7 text-slate-600">
                  {faq.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function FinalCTA({ onStart }) {
  return (
    <section className="mt-16 pb-12 sm:mt-20 sm:pb-16">
      <div className="relative overflow-hidden rounded-[34px] border border-slate-900 bg-slate-900 px-6 py-10 text-center text-white shadow-[0_24px_60px_rgba(15,23,42,0.25)] sm:px-10 sm:py-12">
        <div className="absolute -right-8 top-0 h-44 w-44 rounded-full bg-purple-500/20 blur-3xl" />
        <div className="absolute -left-8 bottom-0 h-44 w-44 rounded-full bg-yellow-400/20 blur-3xl" />

        <div className="relative z-10">
          <h2
            className="text-3xl sm:text-4xl"
            style={{ fontFamily: "'Fredoka One', cursive" }}
          >
            Start your child’s musical journey today
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm font-bold leading-7 text-slate-200 sm:text-base">
            Turning practice into play with fun, beginner-friendly piano songs.
          </p>
          <button
            type="button"
            onClick={onStart}
            className="mt-6 rounded-full border-2 border-yellow-500 bg-gradient-to-r from-yellow-400 to-amber-400 px-8 py-4 text-base font-black text-slate-900 shadow-[0_10px_35px_rgba(251,191,36,0.35)] transition hover:scale-105 active:scale-95"
            style={{ fontFamily: "'Fredoka One', cursive" }}
          >
            Get Started →
          </button>
          <p className="mx-auto mt-4 max-w-3xl text-xs font-bold leading-6 text-slate-300 sm:text-sm">
            Best with a keyboard or piano. Beginner-friendly from day one. No instrument yet?
            Start right away with the virtual piano.
          </p>
        </div>
      </div>
    </section>
  );
}

export default function HomePageOne() {
  const nav = useNavigate();
  const [showFloatingCTA, setShowFloatingCTA] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowFloatingCTA(window.scrollY > 700);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleStart = useMemo(() => () => nav("/game-menu"), [nav]);
  const handleHowItWorks = useMemo(
    () => () => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth", block: "start" }),
    []
  );

  return (
    <div className="relative min-h-screen bg-white text-slate-900">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@700;800;900&display=swap');
        @keyframes slideUp {
          from { transform: translateX(-50%) translateY(90px); opacity: 0; }
          to { transform: translateX(-50%) translateY(0); opacity: 1; }
        }
      `}</style>

      <AnimatedBackground />
      <MenuBar nav={nav} onHowItWorks={handleHowItWorks} />
      <FloatingCTA show={showFloatingCTA} onClick={handleStart} />

      <main className="mx-auto max-w-7xl px-4 pb-8 pt-6 sm:px-5 sm:pb-12 sm:pt-10">
        <HeroVisual />
        <QuickActionBar onStart={handleStart} onHowItWorks={handleHowItWorks} />
        <ProcessSection />
        <ParentValueSection />
        <TestimonialSection />
        <FAQSection />
        <FinalCTA onStart={handleStart} />
      </main>

      <footer className="border-t border-slate-200 bg-slate-50 py-6">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 text-center sm:flex-row sm:px-5 sm:text-left">
          <div>
            <div
              className="text-lg font-black text-slate-900"
              style={{ fontFamily: "'Fredoka One', cursive" }}
            >
              Piano With Pan™
            </div>
            <p className="mt-1 text-xs font-semibold text-slate-500">
              Making beginner piano feel magical, simple, and fun.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 text-xs font-bold text-slate-600 sm:justify-end">
            <button type="button" onClick={handleHowItWorks} className="transition hover:text-slate-900">
              How It Works
            </button>
            <button type="button" onClick={() => nav("/parents")} className="transition hover:text-slate-900">
              For Parents
            </button>
            <button type="button" onClick={handleStart} className="transition hover:text-slate-900">
              Get Started
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
